export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message?: string) {
    super(message ?? code);
    this.status = status;
    this.code = code;
  }
}

let workspaceId: string | null = null;
export const setWorkspace = (id: string | null) => { workspaceId = id; };

/**
 * Identifies this tab for the lifetime of the page.
 *
 * The server broadcasts every change to the whole workspace, this tab
 * included. Without a way to recognise its own echo, a tab would reload the
 * page it is being typed into, a keystroke behind.
 */
export const CLIENT_ID = Array.from(crypto.getRandomValues(new Uint8Array(9)))
  .map((b) => b.toString(36).padStart(2, '0')).join('');

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined && !(body instanceof FormData)) headers['content-type'] = 'application/json';
  if (workspaceId) headers['x-workspace'] = workspaceId;
  headers['x-client'] = CLIENT_ID;

  const res = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
  });

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: any = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }

  if (!res.ok) {
    throw new ApiError(res.status, data?.error ?? 'request_failed', friendly(data?.error) ?? data?.message);
  }
  return data as T;
}

/** Turns server error codes into something worth showing a person. */
function friendly(code?: string): string | undefined {
  if (!code) return undefined;
  const map: Record<string, string> = {
    bad_credentials: 'That ID and password do not match.',
    page_not_found: 'That page no longer exists.',
    block_not_found: 'That block no longer exists.',
    move_to_trash_first: 'Move it to the trash before deleting it for good.',
    cannot_nest_in_itself: 'A page cannot live inside itself.',
    cannot_nest_in_own_child: 'A page cannot live inside one of its own pages.',
    not_a_database: 'That page is not a database.',
    last_view: 'A database keeps at least one view.',
    prop_not_found: 'That property has been removed.',
    view_not_found: 'That view has been removed.',
    file_too_large: 'That file is too large.',
    password_too_short: 'Use at least 8 characters.',
    wrong_password: 'Your current password is incorrect.',
    signup_disabled: 'This workspace is single-account — there is no sign-up.',
    already_setup: 'This workspace already has an account.',
  };
  return map[code] ?? code.replace(/_/g, ' ');
}

export const api = {
  get: <T,>(p: string) => request<T>('GET', p),
  post: <T,>(p: string, body?: unknown) => request<T>('POST', p, body ?? {}),
  patch: <T,>(p: string, body?: unknown) => request<T>('PATCH', p, body ?? {}),
  put: <T,>(p: string, body?: unknown) => request<T>('PUT', p, body ?? {}),
  del: <T,>(p: string) => request<T>('DELETE', p),

  /** Multipart upload with progress, since fetch cannot report it. */
  upload<T>(path: string, form: FormData, onProgress?: (fraction: number) => void): Promise<T> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', path);
      xhr.withCredentials = true;
      if (workspaceId) xhr.setRequestHeader('x-workspace', workspaceId);
      xhr.setRequestHeader('x-client', CLIENT_ID);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
      };
      xhr.onload = () => {
        let data: any = null;
        try { data = JSON.parse(xhr.responseText); } catch { /* non-JSON error page */ }
        if (xhr.status >= 200 && xhr.status < 300) resolve(data as T);
        else reject(new ApiError(xhr.status, data?.error ?? 'upload_failed', friendly(data?.error)));
      };
      xhr.onerror = () => reject(new ApiError(0, 'network_error', 'Upload failed — check your connection.'));
      xhr.send(form);
    });
  },
};
